'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { authClient } from '@bip/api-client';
import { LoginModal } from '../../_components/LoginModal';

interface ShowsRedirectGateProps {
  cartId?: string;
  bookingtype?: string;
  redirectUrl?: string;
}

function mapLegacyPath(p: string): string {
  if (p.startsWith('/shows-booking-detail')) {
    return p.replace('/shows-booking-detail', '/manage-bookings/shows-booking-detail');
  }
  return p;
}

function normaliseRedirectTarget(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Reject protocol-relative URLs
  if (trimmed.startsWith('//')) return null;

  // Plain relative path
  if (trimmed.startsWith('/')) return mapLegacyPath(trimmed);

  // Absolute URL — same origin check
  if (typeof window === 'undefined') return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.origin !== window.location.origin) return null;
    return mapLegacyPath(`${parsed.pathname}${parsed.search}${parsed.hash}`);
  } catch {
    return null;
  }
}

export function ShowsRedirectGate({ cartId, bookingtype, redirectUrl }: ShowsRedirectGateProps) {
  const router = useRouter();

  let targetUrl: string | null = null;
  if (redirectUrl) {
    targetUrl = normaliseRedirectTarget(redirectUrl);
  } else if (cartId && (bookingtype === 'shows' || !bookingtype)) {
    targetUrl = `/manage-bookings/shows-booking-detail?cartId=${encodeURIComponent(cartId)}`;
  }

  const [target, setTarget] = useState<string | null>(targetUrl);
  const [loginOpen, setLoginOpen] = useState(false);
  const actedRef = useRef(false);

  useEffect(() => {
    if (!targetUrl) return;
    setTarget(targetUrl);
    if (actedRef.current) return;
    actedRef.current = true;

    let cancelled = false;
    authClient
      .getMe()
      .then((res) => {
        if (cancelled) return;
        const isLoggedIn = res?.message === 'success';
        if (isLoggedIn) {
          router.push(targetUrl!);
        } else {
          setLoginOpen(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLoginOpen(true);
      });

    return () => {
      cancelled = true;
    };
  }, [targetUrl, router]);

  if (!target) return null;

  return (
    <LoginModal
      isOpen={loginOpen}
      onClose={() => setLoginOpen(false)}
      onReopen={() => setLoginOpen(true)}
    />
  );
}
